import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, Fingerprint, List as DragHandle, Gear, X, BookOpen } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSearchParams } from "react-router-dom";
import Profile from "@/pages/Profile";
import InstallApp from "@/pages/InstallApp";
import AdminPublicContent from "@/pages/AdminPublicContent";
// Iter209ad — Rimosso l'import di MyDomain: il pannello Abbonamenti
// mostra solo la card "IL MIO PIANO" e il wizard di configurazione
// dominio/piano vive nel flusso `/subscription-onboarding`.
import { hasPlatformAuthenticator, registerPasskey } from "@/lib/webauthn";
import ObjectivesAdmin from "@/components/ObjectivesAdmin";
import AdminAuditLog from "@/pages/AdminAuditLog";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter208.99 — Pro Master: professionista NON sub-user (nessun parent_user_id).
  // Solo il master può gestire gli abbonamenti dello studio.
  const isProMaster = user?.role === "professional" && !user?.parent_user_id;
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const defaultTab = requestedTab === "abbonamenti" && isProMaster
    ? "abbonamenti"
    : "profile";
  const [fasce, setFasce] = useState([]);
  const [plafonds, setPlafonds] = useState([]);
  const [profCategories, setProfCategories] = useState([]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    const [f, p, c] = await Promise.all([
      api.get("/fasce"), api.get("/plafonds"), api.get("/professional-categories")
    ]);
    setFasce(f.data); setPlafonds(p.data); setProfCategories(c.data);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const addFascia = async () => { await api.post("/fasce", { name: "Nuova Fascia", discount_percent: 0, description: "" }); load(); };
  const updFascia = async (id, patch) => { await api.patch(`/fasce/${id}`, patch); toast.success("Salvato"); load(); };
  const delFascia = async (id) => { await api.delete(`/fasce/${id}`); load(); };
  // Iter209az — Riordino via drag&drop delle fasce.
  const reorderFasce = async (newList) => {
    setFasce(newList); // optimistic update
    try {
      await api.post("/fasce/reorder", { ids: newList.map((x) => x._id) });
      toast.success("Ordine fasce aggiornato");
    } catch (e) {
      toast.error("Impossibile salvare l'ordine");
      load(); // ripristina lo stato server
    }
  };

  const addPlafond = async () => { await api.post("/plafonds", { name: "Nuovo Plafond", monthly_limit: 500, alert_threshold_percent: 80 }); load(); };
  const updPlafond = async (id, patch) => { await api.patch(`/plafonds/${id}`, patch); toast.success("Salvato"); load(); };
  const delPlafond = async (id) => { await api.delete(`/plafonds/${id}`); load(); };
  // Iter209az — Riordino via drag&drop dei plafond.
  const reorderPlafonds = async (newList) => {
    setPlafonds(newList);
    try {
      await api.post("/plafonds/reorder", { ids: newList.map((x) => x._id) });
      toast.success("Ordine plafond aggiornato");
    } catch (e) {
      toast.error("Impossibile salvare l'ordine");
      load();
    }
  };

  const addProfCategory = async () => { await api.post("/professional-categories", { name: "Nuova Categoria", description: "" }); load(); };
  const updProfCategory = async (id, patch) => { await api.patch(`/professional-categories/${id}`, patch); toast.success("Salvato"); load(); };
  const delProfCategory = async (id) => { await api.delete(`/professional-categories/${id}`); load(); };

  return (
    <div>
      <div className="mb-8">
        <div className="label-eyebrow">Configurazione</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Impostazioni</h1>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-my-profile">Il mio profilo</TabsTrigger>
          <TabsTrigger value="install" data-testid="tab-install-app">Installa app</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Sicurezza</TabsTrigger>
          {isProMaster && <TabsTrigger value="abbonamenti" data-testid="tab-abbonamenti">Abbonamenti</TabsTrigger>}
          {/* Iter208.64 — Tab "Fatturazione" rimossa: i suoi contenuti sono
              disponibili nella pagina Team (tab "Margini"). */}
          {isAdmin && <TabsTrigger value="fasce" data-testid="tab-fasce">Fasce Commerciali</TabsTrigger>}
          {isAdmin && <TabsTrigger value="plafonds" data-testid="tab-plafonds">Plafond</TabsTrigger>}
          {isAdmin && <TabsTrigger value="prof-categories" data-testid="tab-prof-cats">Categorie Professionisti</TabsTrigger>}
          {isAdmin && <TabsTrigger value="goal" data-testid="tab-goal">Obiettivi</TabsTrigger>}
          {/* Iter209ar — Rimossa tab admin "Abbonamenti" (subscription-config).
              La gestione abbonamenti è riservata esclusivamente ai pro master. */}
          {isAdmin && <TabsTrigger value="public-content" data-testid="tab-public-content">Contenuti pubblici</TabsTrigger>}
          {/* Iter209bc — Impostazioni Agenti (admin only) */}
          {isAdmin && <TabsTrigger value="agents" data-testid="tab-agents">Impostazioni Agenti</TabsTrigger>}
          {/* Iter209bk — "Registro attività" spostato qui dalla sidebar */}
          {isAdmin && <TabsTrigger value="audit-log" data-testid="tab-audit-log">Registro attività</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile">
          <Profile embedded />
        </TabsContent>

        <TabsContent value="install">
          <InstallApp />
        </TabsContent>

        <TabsContent value="security">
          <SecurityPanel />
        </TabsContent>

        {isProMaster && (
          <TabsContent value="abbonamenti">
            <div className="mt-6 space-y-6" data-testid="abbonamenti-panel">
              {/* Iter209ad — Il pannello Abbonamenti ora mostra solo la card
                  "IL MIO PIANO" con il pulsante "Cambia piano". Il flusso
                  di configurazione dominio/piano è consolidato nel
                  SubscriptionOnboarding a cui rimanda proprio quel bottone. */}
              <MyPlanBox />
            </div>
          </TabsContent>
        )}

        {/* Iter208.64 — Rimosso <TabsContent value="billing"> */}

        {isAdmin && (
          <TabsContent value="fasce">
            <div className="kpi-card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-display text-xl font-semibold">Fasce Sconto</h3>
                <Button onClick={addFascia} size="sm" className="ht-gradient border-0"><Plus size={14} /> Aggiungi</Button>
              </div>
              {/* Iter209az — Drag&drop per riordinare */}
              <SortableList
                items={fasce}
                onReorder={reorderFasce}
                testIdPrefix="fascia"
                renderRow={(f) => (
                  <>
                    <Input className="col-span-4" defaultValue={f.name} onBlur={e => updFascia(f._id, { ...f, name: e.target.value })} />
                    <Input className="col-span-2 font-mono" type="number" defaultValue={f.discount_percent} onBlur={e => updFascia(f._id, { ...f, discount_percent: Number(e.target.value) })} />
                    <span className="text-xs col-span-1">%</span>
                    <Input className="col-span-3" defaultValue={f.description} onBlur={e => updFascia(f._id, { ...f, description: e.target.value })} />
                    <Button size="sm" variant="ghost" onClick={() => delFascia(f._id)} className="col-span-1"><Trash size={14} /></Button>
                  </>
                )}
              />
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="plafonds">
            <div className="kpi-card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-display text-xl font-semibold">Plafond Mensili</h3>
                <Button onClick={addPlafond} size="sm" className="ht-gradient border-0"><Plus size={14} /> Aggiungi</Button>
              </div>
              {/* Iter209az — Drag&drop per riordinare */}
              <SortableList
                items={plafonds}
                onReorder={reorderPlafonds}
                testIdPrefix="plafond"
                renderRow={(p) => (
                  <>
                    <Input className="col-span-4" defaultValue={p.name} onBlur={e => updPlafond(p._id, { ...p, name: e.target.value })} />
                    <div className="col-span-3">
                      <Label className="text-xs">Limite €</Label>
                      <Input className="font-mono" type="number" defaultValue={p.monthly_limit} onBlur={e => updPlafond(p._id, { ...p, monthly_limit: Number(e.target.value) })} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Alert %</Label>
                      <Input className="font-mono" type="number" defaultValue={p.alert_threshold_percent} onBlur={e => updPlafond(p._id, { ...p, alert_threshold_percent: Number(e.target.value) })} />
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => delPlafond(p._id)} className="col-span-1"><Trash size={14} /></Button>
                  </>
                )}
              />
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="prof-categories">
            <div className="kpi-card">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-display text-xl font-semibold">Categorie Professionisti</h3>
                  <p className="text-sm text-muted-foreground mt-1">Ogni professionista appartiene ad una categoria. I servizi possono essere resi visibili solo ad alcune categorie.</p>
                </div>
                <Button onClick={addProfCategory} size="sm" className="ht-gradient border-0"><Plus size={14} /> Aggiungi</Button>
              </div>
              <div className="space-y-2">
                {profCategories.map(c => (
                  <ProfCategoryRow
                    key={c._id}
                    cat={c}
                    onUpdate={(patch) => updProfCategory(c._id, patch)}
                    onDelete={() => delProfCategory(c._id)}
                  />
                ))}
              </div>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="goal">
            <ObjectivesAdmin />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="public-content">
            <AdminPublicContent />
          </TabsContent>
        )}

        {/* Iter209bc — Impostazioni Agenti */}
        {isAdmin && (
          <TabsContent value="agents">
            <AdminAgentsPanel />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="audit-log">
            <div className="mt-6" data-testid="audit-log-panel">
              <AdminAuditLog embedded />
            </div>
          </TabsContent>
        )}

        {/* Iter209ar — TabsContent "subscription-config" rimosso.
            La configurazione del "Piano consigliato" viene gestita
            ora esclusivamente lato pro master via SubscriptionOnboarding. */}
      </Tabs>
    </div>
  );
}

// ==================== MyPlanBox (Iter209b) ====================
// Box "Il mio piano" mostrato in cima alla tab Abbonamenti.
// Mostra il piano attivo (con fallback su "Gratuito") + tasto per riaprire
// la pagina di onboarding in modalità cambio (`?change=1`).
function MyPlanBox() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/subscription-plans")
      .then((r) => setPlans(r.data?.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);
  const currentId = user?.subscription_plan || "free";
  const current = plans.find((p) => p.id === currentId);
  const startedAt = user?.subscription_started_at;
  const formatStart = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("it-IT", {
        day: "2-digit", month: "long", year: "numeric",
      });
    } catch { return null; }
  };
  return (
    <div
      className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-6"
      data-testid="my-plan-box"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest text-purple-600 font-semibold">
            Il mio piano
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <h3
              className="font-display text-2xl font-bold text-slate-900"
              data-testid="my-plan-name"
            >
              {loading ? "…" : (current?.name || "Gratuito")}
            </h3>
            {current && (
              <span className="text-sm text-slate-500">
                {current.monthly === 0
                  ? "€ 0 / mese"
                  : `€ ${current.monthly.toFixed(2).replace(".", ",")} / mese`}
              </span>
            )}
          </div>
          {current?.description && (
            <p className="text-sm text-slate-600 mt-2 max-w-xl">
              {current.description}
            </p>
          )}
          {formatStart(startedAt) && (
            <p className="text-[11px] text-slate-400 mt-3">
              Attivo dal <strong>{formatStart(startedAt)}</strong>
            </p>
          )}
        </div>
        <Button
          onClick={() => { window.location.href = "/subscription-onboarding?change=1"; }}
          data-testid="my-plan-change-btn"
          className="shrink-0"
        >
          Cambia piano
        </Button>
      </div>
    </div>
  );
}



// ==================== SubscriptionConfigPanel (Iter209ar — rimosso) ====================
// La configurazione del "Piano consigliato" era una feature admin
// (`Settings → tab "Abbonamenti"`). Iter209ar ha rimosso quella tab
// perché la gestione abbonamenti è riservata esclusivamente ai pro
// master (via SubscriptionOnboarding). Il pannello e i relativi
// endpoint admin `/admin/subscription-config` restano lato backend
// come single-source-of-truth ma non hanno più UI dedicata.



// ==================== SecurityPanel ====================
function SecurityPanel() {
  const { user, setUser } = useAuth();
  const [supported, setSupported] = useState(null);
  const [creds, setCreds] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadCreds = async () => {
    try {
      const r = await api.get("/auth/webauthn/credentials");
      setCreds(r.data);
    } catch (err) {
      // Non blocca la UI: l'utente potrebbe non avere ancora passkey registrate
      if (process.env.NODE_ENV !== "production") {
        console.warn("[settings] loadCreds failed:", err?.message || err);
      }
    }
  };

  useEffect(() => {
    hasPlatformAuthenticator().then(setSupported);
    loadCreds();
  }, []);

  const addPasskey = async () => {
    if (!name.trim()) return toast.error("Dai un nome al dispositivo (es. iPhone di Andrea)");
    setBusy(true);
    try {
      await registerPasskey(name.trim());
      toast.success("Passkey aggiunta ✓");
      setName("");
      loadCreds();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || err?.message || "Errore registrazione passkey";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Vuoi davvero eliminare questa passkey? Non potrai più usarla per accedere.")) return;
    try {
      await api.delete(`/auth/webauthn/credentials/${encodeURIComponent(id)}`);
      toast.success("Passkey eliminata");
      loadCreds();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const resetPasskeyPrompt = async () => {
    setResetting(true);
    try {
      await api.post("/auth/passkey-prompt/reset");
      // Aggiorna user locale (persist + context)
      const me = await api.get("/auth/me");
      if (setUser) setUser(me.data);
      try {
        localStorage.setItem("ht_user", JSON.stringify(me.data));
        // Rimuove anche il dismiss di sessione, così il prompt riappare subito al prossimo login
        sessionStorage.removeItem("ht_passkey_prompt_dismissed_session");
      } catch (_) { /* ignore */ }
      toast.success("Prompt riattivato. Al prossimo accesso da mobile ti chiederemo di attivare la passkey.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="security-panel">
      <div className="kpi-card">
        <div className="flex items-center gap-2 mb-1">
          <Fingerprint size={22} color="#6D28D9" weight="duotone" />
          <h3 className="font-display text-xl font-semibold">Accesso biometrico (Passkey)</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Accedi con <strong>Face ID</strong>, <strong>Touch ID</strong>, <strong>impronta</strong> o <strong>Windows Hello</strong>.
          Le passkey sono più sicure delle password e sostituiscono anche il codice 2FA.
        </p>

        {supported === false && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
            ⚠️ Il tuo dispositivo o browser non supporta l'autenticazione biometrica.
            Puoi ancora usare passkey da un altro dispositivo tramite QR code / iCloud Keychain / Google Password Manager.
          </div>
        )}
        {supported === true && (
          <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
            ✓ Il tuo dispositivo supporta l'autenticazione biometrica.
          </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Nome dispositivo (es. iPhone di Mario)"
            className="flex-1"
            data-testid="passkey-name-input"
          />
          <Button
            onClick={addPasskey}
            disabled={busy || !name.trim()}
            className="ht-gradient border-0"
            data-testid="passkey-add-btn">
            <Fingerprint size={16} className="mr-2" />
            {busy ? "Aggiungo…" : "Aggiungi passkey"}
          </Button>
        </div>

        {/* Prompt automatico enrollment (visibile solo se dismisso permanente) */}
        {user?.passkey_prompt_dismissed && creds.length === 0 && (
          <div className="mt-4 rounded-xl border border-purple-200 bg-purple-50/60 p-4 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid="passkey-prompt-reset-card">
            <div className="flex-1 text-xs">
              <div className="font-semibold text-purple-900">Prompt automatico disattivato</div>
              <div className="text-purple-800/80 mt-1">
                Hai scelto di non ricevere più il suggerimento di attivare la passkey al login. Puoi riabilitarlo qui.
              </div>
            </div>
            <Button
              onClick={resetPasskeyPrompt}
              disabled={resetting}
              size="sm"
              variant="outline"
              className="border-purple-300 text-purple-800 hover:bg-purple-100"
              data-testid="passkey-prompt-reset-btn"
            >
              {resetting ? "Attivazione…" : "Riattiva prompt"}
            </Button>
          </div>
        )}
      </div>

      {creds.length > 0 && (
        <div className="kpi-card">
          <h3 className="font-display text-lg font-semibold mb-3">Le tue passkey</h3>
          <table className="w-full text-sm mobile-cards">
            <thead className="border-b text-xs uppercase text-muted-foreground">
              <tr className="text-left">
                <th className="py-2">Nome</th>
                <th>Creata</th>
                <th>Ultimo utilizzo</th>
                <th className="text-right pr-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {creds.map(c => (
                <tr key={c.id} className="border-b" data-testid={`passkey-row-${c.id.slice(0, 8)}`}>
                  <td data-label="Nome" className="py-3 font-medium">{c.name}</td>
                  <td data-label="Creata" className="text-xs text-muted-foreground">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td data-label="Ultimo utilizzo" className="text-xs text-muted-foreground">
                    {c.last_used_at ? new Date(c.last_used_at).toLocaleString("it-IT") : "Mai"}
                  </td>
                  <td data-label="Azioni" className="text-right pr-2 mobile-actions">
                    <Button size="sm" variant="ghost" onClick={() => removePasskey(c.id)}
                      className="text-red-600 hover:text-red-700" data-testid={`passkey-delete-${c.id.slice(0, 8)}`}>
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}




// ==================== ProfCategoryRow ====================
// Riga di gestione categoria professionista con editor persona/tag Chatbot Pro.
// Ogni categoria può avere una "persona" custom (prompt che guida il tono e il
// focus del chatbot quando parla con utenti di questa categoria) e una lista di
// tag KB che vengono usati come boost nel retrieval BM25.
function ProfCategoryRow({ cat, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(cat.name || "");
  const [description, setDescription] = useState(cat.description || "");
  const [persona, setPersona] = useState(cat.chatbot_persona || "");
  const [tagsRaw, setTagsRaw] = useState((cat.chatbot_kb_tags || []).join(", "));
  // Dashboard type (iter50) — dropdown a 4 valori
  const [dashType, setDashType] = useState(cat.dashboard_type || "");

  const savePersona = async () => {
    const tags = tagsRaw.split(",").map(t => t.trim()).filter(Boolean);
    await onUpdate({
      name, description,
      chatbot_persona: persona,
      chatbot_kb_tags: tags,
    });
  };

  return (
    <div className="border rounded-lg p-3 space-y-2 bg-white" data-testid={`prof-cat-row-${cat._id}`}>
      <div className="grid grid-cols-12 gap-2 items-center">
        <Input
          className="col-span-4"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => onUpdate({ name, description })}
          data-testid={`prof-cat-name-${cat._id}`}
        />
        <Input
          className="col-span-4"
          value={description}
          placeholder="Descrizione (uso interno)"
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => onUpdate({ name, description })}
        />
        <select
          className="col-span-2 border rounded px-2 py-1.5 text-xs bg-white"
          value={dashType}
          onChange={(e) => {
            const v = e.target.value;
            setDashType(v);
            onUpdate({ name, description, dashboard_type: v });
          }}
          data-testid={`prof-cat-dashboard-type-${cat._id}`}
          title="Tipo di dashboard specifica per questa categoria"
        >
          <option value="">Dashboard standard</option>
          <option value="tecnica">Studio Tecnico</option>
          <option value="notaio">Studio Notarile</option>
          <option value="agenzia">Agenzia Immobiliare</option>
        </select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setExpanded(v => !v)}
          className="col-span-1"
          data-testid={`prof-cat-expand-${cat._id}`}
        >
          {expanded ? "Chiudi" : "🤖 Chatbot"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          className="col-span-1"
        >
          <Trash size={14} />
        </Button>
      </div>
      {expanded && (
        <div className="pt-2 mt-2 border-t space-y-3">
          <div>
            <Label className="text-sm font-semibold">Persona dell'Agente AI</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Prompt custom che definisce tono, competenze e focus del chatbot quando parla con
              utenti di questa categoria. Lascialo vuoto per usare la persona di default
              (riconosciuta automaticamente dal nome: "Agenzia Immobiliare", "Studio Tecnico",
              "Geometra", "Notaio", "Amministratore").
            </p>
            <textarea
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              placeholder={"Es. \"Sei un assistente commerciale per agenzia immobiliare. Focus: contratti, preliminari, mandati di vendita, provvigioni, antiriciclaggio (D.Lgs. 231/2007). Rispondi calibrando gli esempi sul lavoro quotidiano dell'agente immobiliare.\""}
              rows={5}
              className="w-full border rounded-md px-3 py-2 text-sm font-mono"
              data-testid={`prof-cat-persona-${cat._id}`}
            />
          </div>
          <div>
            <Label className="text-sm font-semibold">Tag KB prioritari</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Lista di parole/tag (separati da virgola) usati per prioritizzare i documenti
              della knowledge base tecnica pertinenti a questa categoria. Es. per Studio Tecnico:
              <code className="mx-1 px-1 bg-slate-100 rounded">dpr 380, sanatoria, scia, urbanistica</code>.
              Deve corrispondere ai <em>tag</em> assegnati ai documenti in Agente AI → Knowledge Base.
            </p>
            <Input
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="dpr 380, sanatoria, scia, urbanistica"
              data-testid={`prof-cat-kb-tags-${cat._id}`}
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={savePersona}
              className="ht-gradient border-0"
              data-testid={`prof-cat-save-persona-${cat._id}`}
            >
              Salva persona &amp; tag
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== SortableList (Iter209az) ====================
/**
 * Iter209az — Lista riordinabile via drag&drop condivisa fra le
 * schede "Fasce Commerciali" e "Plafond".
 *
 * Props:
 *   - items: array con `_id` (source-of-truth locale).
 *   - onReorder(newList): chiamato al termine del drag con la lista
 *     riordinata; l'implementazione outer si occupa di persistere
 *     l'ordine tramite `POST /{fasce|plafonds}/reorder`.
 *   - renderRow(item): contenuto delle celle centrali del grid a
 *     12 colonne (l'handle di drag è già gestito qui).
 *   - testIdPrefix: prefisso per testid `-drag-<id>` / `-row-<id>`.
 */
function SortableList({ items, onReorder, renderRow, testIdPrefix }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((x) => x._id === active.id);
    const newIndex = items.findIndex((x) => x._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((x) => x._id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid={`${testIdPrefix}-sortable-list`}>
          {items.map((it) => (
            <SortableRow key={it._id} item={it} testIdPrefix={testIdPrefix} renderRow={renderRow} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ item, testIdPrefix, renderRow }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "hsl(var(--surface-alt))" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-12 gap-2 items-center border-b pb-2"
      data-testid={`${testIdPrefix}-row-${item._id}`}
    >
      <span
        {...attributes}
        {...listeners}
        className="col-span-1 flex items-center justify-center cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 select-none"
        title="Trascina per riordinare"
        aria-label={`Trascina ${item.name || item._id}`}
        data-testid={`${testIdPrefix}-drag-${item._id}`}
      >
        <DragHandle size={16} weight="bold" />
      </span>
      {renderRow(item)}
    </div>
  );
}


// ==================== AdminAgentsPanel (Iter209bc → Iter209bd/be) ====================
/**
 * Iter209bc — Sezione admin "Impostazioni Agenti" (create/toggle/delete).
 * Iter209bd — Builder di comportamento: istruzioni personalizzate, tono,
 *   lingua, orario, fallback + email admin, regole di canale (email = solo
 *   supporto, WhatsApp = prenotazioni con riconoscimento numero mittente).
 * Iter209be — L'agente legge le FAQ dalla pagina "Chatbot Pro" (KB
 *   `chatbot_technical_knowledge`). L'admin può fare preview della KB e
 *   del prompt che l'agente userà.
 */
function AdminAgentsPanel() {
  const [agents, setAgents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState({ name: "Agente Reply", kind: "reply", whatsapp_phone: "", email: "" });
  const [configAgent, setConfigAgent] = React.useState(null); // agente aperto nel modale

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/agents");
      setAgents(r.data?.agents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento agenti");
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Nome agente obbligatorio");
    setCreating(true);
    try {
      await api.post("/admin/agents", {
        name: form.name.trim(),
        kind: form.kind || "reply",
        whatsapp_phone: form.whatsapp_phone.trim() || null,
        email: form.email.trim() || null,
        enabled: false,
      });
      toast.success("Agente creato");
      setForm({
        name: form.kind === "social" ? "Agente Reply" : "Agente Social",
        kind: form.kind === "social" ? "reply" : "reply",
        whatsapp_phone: "", email: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione agente");
    } finally { setCreating(false); }
  };

  const patch = async (id, updates) => {
    try {
      const r = await api.patch(`/admin/agents/${id}`, updates);
      setAgents((prev) => prev.map((a) => (a.id === id ? r.data : a)));
      // se il modale è aperto su questo agente, aggiorna anche lì
      setConfigAgent((prev) => (prev && prev.id === id ? r.data : prev));
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
      load();
      throw e;
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo agente?")) return;
    try {
      await api.delete(`/admin/agents/${id}`);
      toast.success("Agente eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-agents-panel">
      <div className="kpi-card">
        <h3 className="font-display text-xl font-semibold mb-1">Nuovo agente</h3>
        <p className="text-xs text-slate-500 mb-4">
          Crea un agente automatico Housetag. <b>Agente Reply</b>: chat WhatsApp/email
          per FAQ + prenotazioni. <b>Agente Social</b>: propone e pubblica post su
          LinkedIn/Instagram/Facebook con approvazione.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Tipo agente</Label>
            <select
              value={form.kind}
              onChange={(e) => {
                const kind = e.target.value;
                setForm({
                  ...form,
                  kind,
                  name: kind === "social" ? "Agente Social" : "Agente Reply",
                });
              }}
              className="w-full h-10 border rounded-md px-3 text-sm"
              data-testid="agent-create-kind">
              <option value="reply">Agente Reply</option>
              <option value="social">Agente Social</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Nome agente</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="agent-create-name"
              placeholder={form.kind === "social" ? "Agente Social" : "Agente Reply"}
            />
          </div>
          <div>
            <Label className="text-xs">
              {form.kind === "social" ? "Contatto WhatsApp (opz.)" : "Numero WhatsApp Business"}
            </Label>
            <Input
              value={form.whatsapp_phone}
              onChange={(e) => setForm({ ...form, whatsapp_phone: e.target.value })}
              data-testid="agent-create-phone"
              placeholder="+39 366 1234567"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="agent-create-email"
              placeholder={form.kind === "social" ? "agente-social@housetag.it" : "agente-reply@housetag.it"}
            />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button
            onClick={create}
            disabled={creating}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="agent-create-submit"
          >
            <Plus size={14} /> {creating ? "Creazione…" : "Crea agente"}
          </Button>
        </div>
      </div>

      <div className="kpi-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold">Agenti configurati</h3>
          <span className="text-xs text-slate-500">{agents.length} totali</span>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Caricamento…</p>
        ) : agents.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="agents-empty">
            Nessun agente ancora creato.
          </p>
        ) : (
          <ul className="divide-y" data-testid="agents-list">
            {agents.map((a) => (
              <li key={a.id} className="grid grid-cols-12 items-center gap-3 py-3" data-testid={`agent-row-${a.id}`}>
                <div className="col-span-3 flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${a.kind === "social" ? "bg-pink-100 text-pink-700" : "bg-purple-100 text-purple-700"}`}
                    data-testid={`agent-kind-${a.id}`}>
                    {a.kind === "social" ? "SOCIAL" : "REPLY"}
                  </span>
                  <Input className="flex-1" defaultValue={a.name}
                    onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== a.name && patch(a.id, { name: e.target.value.trim() })}
                    data-testid={`agent-name-${a.id}`} />
                </div>
                <Input className="col-span-3" defaultValue={a.whatsapp_phone}
                  onBlur={(e) => e.target.value !== a.whatsapp_phone && patch(a.id, { whatsapp_phone: e.target.value })}
                  placeholder="Nessun numero"
                  data-testid={`agent-phone-${a.id}`} />
                <Input className="col-span-3" defaultValue={a.email}
                  onBlur={(e) => e.target.value !== a.email && patch(a.id, { email: e.target.value })}
                  placeholder="Nessuna email"
                  data-testid={`agent-email-${a.id}`} />
                <div className="col-span-1 flex items-center justify-center">
                  <Switch
                    checked={a.enabled}
                    onCheckedChange={(v) => patch(a.id, { enabled: !!v })}
                    data-testid={`agent-enabled-${a.id}`}
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => setConfigAgent(a)}
                    data-testid={`agent-configure-${a.id}`}
                    className="text-xs">
                    <Gear size={14} /> Configura
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                    data-testid={`agent-delete-${a.id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {configAgent && configAgent.kind === "social" && (
        <SocialAgentConfigModal
          agent={configAgent}
          onClose={() => setConfigAgent(null)}
          onSave={(updates) => patch(configAgent.id, updates)}
        />
      )}
      {configAgent && configAgent.kind !== "social" && (
        <AgentConfigModal
          agent={configAgent}
          onClose={() => setConfigAgent(null)}
          onSave={(updates) => patch(configAgent.id, updates)}
        />
      )}
    </div>
  );
}

// ---------- Modale configurazione agente (Iter209bd/be) ----------
function AgentConfigModal({ agent, onClose, onSave }) {
  const [form, setForm] = React.useState({
    instructions: agent.instructions || "",
    tone: agent.tone || "amichevole",
    language: agent.language || "it",
    active_hours: agent.active_hours || "24/7",
    fallback_message: agent.fallback_message || "Ti ricontatteremo a breve.",
    unknown_phone_message: agent.unknown_phone_message || "Non riesco a identificarti con questo numero. Assicurati di scrivere dal numero con cui ti sei iscritto a Housetag.",
    admin_notify_email: agent.admin_notify_email || "",
    email_allow_booking: !!agent.email_allow_booking,
    whatsapp_allow_booking: agent.whatsapp_allow_booking !== false,
    require_phone_recognition: agent.require_phone_recognition !== false,
    kb_source: agent.kb_source || "chatbot_pro",
  });
  const [saving, setSaving] = React.useState(false);
  const [kbPreview, setKbPreview] = React.useState(null);
  const [loadingKb, setLoadingKb] = React.useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success("Configurazione salvata");
      onClose();
    } catch (_) {} finally { setSaving(false); }
  };

  const loadKb = async () => {
    setLoadingKb(true);
    try {
      const r = await api.get(`/admin/agents/${agent.id}/kb-preview`);
      setKbPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento KB");
    } finally { setLoadingKb(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="agent-config-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <div className="text-xs text-slate-500">Configurazione</div>
            <h3 className="font-display text-xl font-semibold">{agent.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" data-testid="agent-config-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Istruzioni personalizzate */}
          <section>
            <Label className="text-sm font-semibold">Istruzioni personalizzate</Label>
            <p className="text-xs text-slate-500 mb-2">
              Definisci il comportamento dell'agente: cosa deve fare, cosa non deve fare, esempi.
            </p>
            <textarea
              className="w-full min-h-[120px] rounded-md border border-slate-200 p-2 text-sm"
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="Es. Sei l'Agente Reply di Housetag. Rispondi alle domande sui piani e sui servizi con tono amichevole…"
              data-testid="agent-config-instructions"
            />
          </section>

          {/* Tono / Lingua / Orario */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Tono</Label>
              <Input value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}
                data-testid="agent-config-tone" placeholder="amichevole" />
            </div>
            <div>
              <Label className="text-xs">Lingua</Label>
              <Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}
                data-testid="agent-config-language" placeholder="it" />
            </div>
            <div>
              <Label className="text-xs">Operatività</Label>
              <Input value={form.active_hours} onChange={(e) => setForm({ ...form, active_hours: e.target.value })}
                data-testid="agent-config-hours" placeholder="24/7" />
            </div>
          </section>

          {/* Fallback + email admin */}
          <section className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Fallback</Label>
              <p className="text-xs text-slate-500 mb-2">
                Messaggio inviato all'utente quando l'agente non sa rispondere. L'intera conversazione viene inoltrata all'email admin qui sotto.
              </p>
              <Input value={form.fallback_message}
                onChange={(e) => setForm({ ...form, fallback_message: e.target.value })}
                data-testid="agent-config-fallback"
                placeholder="Ti ricontatteremo a breve." />
            </div>
            <div>
              <Label className="text-xs">Email admin per notifiche fallback</Label>
              <Input type="email" value={form.admin_notify_email}
                onChange={(e) => setForm({ ...form, admin_notify_email: e.target.value })}
                data-testid="agent-config-admin-email"
                placeholder="admin@tuazienda.it" />
            </div>
          </section>

          {/* Regole di canale */}
          <section className="space-y-3">
            <Label className="text-sm font-semibold">Regole di canale</Label>

            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Email — solo supporto</div>
                  <div className="text-xs text-slate-500">
                    Via email l'agente gestisce solo comunicazione e supporto. Le prenotazioni sono disabilitate.
                  </div>
                </div>
                <Switch
                  checked={form.email_allow_booking}
                  onCheckedChange={(v) => setForm({ ...form, email_allow_booking: !!v })}
                  data-testid="agent-config-email-booking"
                />
              </div>
              {form.email_allow_booking && (
                <div className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  ⚠️ Prenotazioni via email abilitate. Sconsigliato: non c'è verifica del mittente.
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">WhatsApp — prenotazioni</div>
                  <div className="text-xs text-slate-500">
                    Le prenotazioni avvengono esclusivamente da WhatsApp.
                  </div>
                </div>
                <Switch
                  checked={form.whatsapp_allow_booking}
                  onCheckedChange={(v) => setForm({ ...form, whatsapp_allow_booking: !!v })}
                  data-testid="agent-config-wa-booking"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Verifica numero mittente</div>
                  <div className="text-xs text-slate-500">
                    Il numero WhatsApp deve corrispondere a un utente Housetag registrato.
                  </div>
                </div>
                <Switch
                  checked={form.require_phone_recognition}
                  onCheckedChange={(v) => setForm({ ...form, require_phone_recognition: !!v })}
                  data-testid="agent-config-phone-check"
                />
              </div>
              <div>
                <Label className="text-xs">Messaggio per numero non riconosciuto</Label>
                <Input value={form.unknown_phone_message}
                  onChange={(e) => setForm({ ...form, unknown_phone_message: e.target.value })}
                  data-testid="agent-config-unknown-phone-msg" />
              </div>
            </div>
          </section>

          {/* KB — Chatbot Pro */}
          <section className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen size={16} className="text-purple-700" />
              <div className="text-sm font-semibold text-purple-900">Base di conoscenza (FAQ)</div>
            </div>
            <p className="text-xs text-slate-600">
              L'agente legge le FAQ e le risposte predefinite dalla pagina <b>Agente AI</b>. Aggiornando quella pagina, l'agente si aggiorna automaticamente.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={loadKb}
                data-testid="agent-config-kb-preview">
                {loadingKb ? "Caricamento…" : "Anteprima KB e prompt"}
              </Button>
              {kbPreview && (
                <span className="text-xs text-slate-600" data-testid="agent-config-kb-count">
                  {kbPreview.kb_count} voci caricate
                </span>
              )}
            </div>
            {kbPreview && (
              <div className="mt-2 space-y-2 max-h-[240px] overflow-y-auto">
                {kbPreview.kb_entries.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Nessuna voce nella KB Agente AI. Aggiungi contenuti dalla pagina Agente AI.
                  </p>
                ) : (
                  kbPreview.kb_entries.map((e) => (
                    <div key={e.id} className="text-xs bg-white rounded border border-slate-200 p-2">
                      <div className="font-medium">{e.title} <span className="text-slate-400">· {e.kind}</span></div>
                      <div className="text-slate-600 line-clamp-3 mt-1">{e.content}</div>
                    </div>
                  ))
                )}
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Vedi prompt sistema (WhatsApp)</summary>
                  <pre className="whitespace-pre-wrap text-[10px] bg-slate-900 text-slate-100 rounded p-2 mt-1" data-testid="agent-config-prompt-preview">{kbPreview.prompt_whatsapp}</pre>
                </details>
              </div>
            )}
          </section>

          {/* Iter209bg — Test WhatsApp booking */}
          <WhatsAppBookingTester agent={agent} />
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-slate-50 rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} data-testid="agent-config-cancel">Annulla</Button>
          <Button onClick={save} disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="agent-config-save">
            {saving ? "Salvataggio…" : "Salva configurazione"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- WhatsApp booking tester (Iter209bg) ----------
function WhatsAppBookingTester({ agent }) {
  const [phone, setPhone] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [thread, setThread] = React.useState([]); // [{role, text}]
  const [busy, setBusy] = React.useState(false);

  const send = async () => {
    if (!phone.trim() || !message.trim()) {
      toast.error("Inserisci numero e messaggio");
      return;
    }
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: message }]);
    try {
      const r = await api.post(`/admin/agents/${agent.id}/bot/message`, {
        phone: phone.trim(),
        message,
      });
      setThread((t) => [...t, { role: "agent", text: r.data?.reply || "(nessuna risposta)", action: r.data?.action }]);
      setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio messaggio");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!phone.trim()) return;
    try {
      await api.post(`/admin/agents/${agent.id}/bot/reset`, { phone: phone.trim() });
      setThread([]);
      toast.success("Sessione azzerata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <section className="rounded-lg border border-green-200 bg-green-50/40 p-3 space-y-2" data-testid="wa-booking-tester">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-green-900">Test conversazione WhatsApp (booking)</div>
      </div>
      <p className="text-xs text-slate-600">
        Simula un messaggio WhatsApp. L'agente verifica il numero, propone slot dal calendario condiviso e conferma la prenotazione.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+39 333 1234567"
          data-testid="wa-tester-phone"
        />
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder='es. "vorrei un appuntamento domani"'
          onKeyDown={(e) => e.key === "Enter" && send()}
          className="md:col-span-2"
          data-testid="wa-tester-message"
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={send} disabled={busy}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="wa-tester-send">
          {busy ? "…" : "Invia"}
        </Button>
        <Button size="sm" variant="outline" onClick={reset}
          data-testid="wa-tester-reset">
          Reset
        </Button>
      </div>
      {thread.length > 0 && (
        <div className="mt-2 max-h-[240px] overflow-y-auto space-y-2 bg-white rounded border border-slate-200 p-2"
          data-testid="wa-tester-thread">
          {thread.map((m, i) => (
            <div key={i} className={`text-xs p-2 rounded ${m.role === "user" ? "bg-slate-100 self-end" : "bg-green-100"}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                {m.role === "user" ? "Utente" : `Agente${m.action ? " · " + m.action : ""}`}
              </div>
              <div className="whitespace-pre-wrap">{m.text}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- Modale configurazione Agente Social (Iter209bh) ----------
function SocialAgentConfigModal({ agent, onClose, onSave }) {
  const [form, setForm] = React.useState({
    tone: agent.tone || "amichevole, professionale",
    language: agent.language || "it",
    topics: (agent.topics && agent.topics.length ? agent.topics : [
      "progressi", "espansione", "clienti", "soddisfazione", "cosa_fa_housetag", "costi_e_piani",
    ]),
    autonomy_mode: agent.autonomy_mode || "supervised",
    social_connections: agent.social_connections || {
      facebook: { page_id: "", page_token: "" },
      instagram: { ig_id: "", page_token: "" },
      linkedin: { author_urn: "", access_token: "" },
    },
  });
  const [saving, setSaving] = React.useState(false);
  const [genTopic, setGenTopic] = React.useState("cosa_fa_housetag");
  const [genPlatforms, setGenPlatforms] = React.useState({ facebook: true, instagram: true, linkedin: true });
  const [genHint, setGenHint] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const [posts, setPosts] = React.useState([]);

  const conn = form.social_connections || {};
  const upd = (path, value) => {
    setForm((f) => {
      const next = { ...f, social_connections: { ...(f.social_connections || {}) } };
      const [k1, k2] = path.split(".");
      next.social_connections[k1] = { ...(next.social_connections[k1] || {}), [k2]: value };
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        tone: form.tone,
        language: form.language,
        topics: form.topics,
        autonomy_mode: form.autonomy_mode,
        social_connections: form.social_connections,
      });
      toast.success("Configurazione salvata");
    } catch (_) {} finally { setSaving(false); }
  };

  const loadQueue = React.useCallback(async () => {
    try {
      const r = await api.get(`/admin/social-posts?agent_id=${agent.id}`);
      setPosts(r.data?.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento coda");
    }
  }, [agent.id]);
  React.useEffect(() => { loadQueue(); }, [loadQueue]);

  const generate = async () => {
    const plats = Object.entries(genPlatforms).filter(([, v]) => v).map(([k]) => k);
    if (plats.length === 0) return toast.error("Seleziona almeno una piattaforma");
    setGenerating(true);
    try {
      await api.post(`/admin/agents/${agent.id}/social/generate`, {
        topic: genTopic,
        platforms: plats,
        hint: genHint.trim() || null,
      });
      toast.success("Bozze generate");
      setGenHint("");
      loadQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione");
    } finally { setGenerating(false); }
  };

  const approvePost = async (id) => {
    try {
      const r = await api.post(`/admin/social-posts/${id}/approve`);
      const pr = r.data?.publish_result;
      if (pr?.ok) toast.success("Pubblicato");
      else toast.warning(pr?.error || "Approvato (pubblicazione non riuscita)");
      loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore approvazione"); }
  };
  const rejectPost = async (id) => {
    try {
      await api.post(`/admin/social-posts/${id}/reject`, { reason: "" });
      toast.success("Post rifiutato");
      loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const patchPost = async (id, content) => {
    try {
      await api.patch(`/admin/social-posts/${id}`, { content });
      loadQueue();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="social-agent-config-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <div className="text-xs text-pink-600 uppercase font-semibold tracking-wider">Agente Social</div>
            <h3 className="font-display text-xl font-semibold">{agent.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100" data-testid="social-config-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Profili collegati */}
          <section>
            <Label className="text-sm font-semibold">Profili social collegati</Label>
            <p className="text-xs text-slate-500 mb-3">
              Incolla gli ID e i token OAuth ottenuti dalle rispettive dashboard developer. Se lasci vuoti, i post verranno approvati senza pubblicazione reale.
            </p>
            <div className="space-y-3">
              {/* Facebook */}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold mb-2">Facebook Page</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Page ID" value={conn.facebook?.page_id || ""}
                    onChange={(e) => upd("facebook.page_id", e.target.value)}
                    data-testid="social-fb-page-id" />
                  <Input placeholder="Page Access Token" value={conn.facebook?.page_token || ""}
                    onChange={(e) => upd("facebook.page_token", e.target.value)}
                    data-testid="social-fb-page-token" />
                </div>
              </div>
              {/* Instagram */}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold mb-2">Instagram Business</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Instagram Business ID" value={conn.instagram?.ig_id || ""}
                    onChange={(e) => upd("instagram.ig_id", e.target.value)}
                    data-testid="social-ig-id" />
                  <Input placeholder="Page Access Token (stesso di FB)" value={conn.instagram?.page_token || ""}
                    onChange={(e) => upd("instagram.page_token", e.target.value)}
                    data-testid="social-ig-token" />
                </div>
              </div>
              {/* LinkedIn */}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold mb-2">LinkedIn (Organization o Personal)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Author URN (urn:li:organization:...)" value={conn.linkedin?.author_urn || ""}
                    onChange={(e) => upd("linkedin.author_urn", e.target.value)}
                    data-testid="social-li-urn" />
                  <Input placeholder="Access Token" value={conn.linkedin?.access_token || ""}
                    onChange={(e) => upd("linkedin.access_token", e.target.value)}
                    data-testid="social-li-token" />
                </div>
              </div>
            </div>
          </section>

          {/* Autonomia */}
          <section>
            <Label className="text-sm font-semibold">Livello di autonomia</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <label className={`p-3 rounded-lg border cursor-pointer text-sm ${form.autonomy_mode === "supervised" ? "border-pink-500 bg-pink-50" : "border-slate-200"}`}>
                <input type="radio" name="autonomy" value="supervised"
                  checked={form.autonomy_mode === "supervised"}
                  onChange={() => setForm({ ...form, autonomy_mode: "supervised" })}
                  data-testid="social-autonomy-supervised" className="mr-2" />
                <b>Supervisionato</b> — l'agente propone, tu approvi ogni post prima della pubblicazione.
              </label>
              <label className={`p-3 rounded-lg border cursor-pointer text-sm ${form.autonomy_mode === "autonomous" ? "border-pink-500 bg-pink-50" : "border-slate-200"}`}>
                <input type="radio" name="autonomy" value="autonomous"
                  checked={form.autonomy_mode === "autonomous"}
                  onChange={() => setForm({ ...form, autonomy_mode: "autonomous" })}
                  data-testid="social-autonomy-autonomous" className="mr-2" />
                <b>Autonomo</b> — l'agente pubblica direttamente sui temi predefiniti.
              </label>
            </div>
          </section>

          {/* Composer */}
          <section className="rounded-lg border border-pink-200 bg-pink-50/40 p-3">
            <div className="text-sm font-semibold mb-2">Genera bozze con Claude Sonnet 5</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <div>
                <Label className="text-xs">Argomento</Label>
                <select value={genTopic} onChange={(e) => setGenTopic(e.target.value)}
                  className="w-full h-10 border rounded-md px-3 text-sm" data-testid="social-gen-topic">
                  <option value="progressi">Progressi aziendali</option>
                  <option value="espansione">Espansione</option>
                  <option value="clienti">Numero clienti/utenti</option>
                  <option value="soddisfazione">Soddisfazione clienti</option>
                  <option value="cosa_fa_housetag">Cosa fa Housetag</option>
                  <option value="costi_e_piani">Costi e piani</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Note per l'agente (opz.)</Label>
                <Input value={genHint} onChange={(e) => setGenHint(e.target.value)}
                  placeholder="es. Annuncio apertura nuova città Torino"
                  data-testid="social-gen-hint" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 items-center mb-2 text-sm">
              {["facebook","instagram","linkedin"].map((p) => (
                <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={!!genPlatforms[p]}
                    onChange={(e) => setGenPlatforms({ ...genPlatforms, [p]: e.target.checked })}
                    data-testid={`social-gen-${p}`} />
                  <span className="capitalize">{p}</span>
                </label>
              ))}
              <div className="flex-1" />
              <Button size="sm" onClick={generate} disabled={generating}
                className="bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="social-gen-submit">
                {generating ? "Generazione…" : "Genera bozze"}
              </Button>
            </div>
          </section>

          {/* Coda post */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Coda post</div>
              <button onClick={loadQueue} className="text-xs text-slate-500 hover:text-slate-800 underline"
                data-testid="social-queue-refresh">Aggiorna</button>
            </div>
            {posts.length === 0 ? (
              <p className="text-xs text-slate-500" data-testid="social-queue-empty">Nessun post generato.</p>
            ) : (
              <div className="space-y-2" data-testid="social-queue">
                {posts.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 p-3 space-y-2"
                    data-testid={`social-post-${p.id}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="uppercase font-semibold text-pink-700">{p.platform}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{p.topic}</span>
                      <span className="flex-1" />
                      <span className={`px-2 py-0.5 rounded uppercase text-[10px] font-semibold ${
                        p.status === "published" ? "bg-green-100 text-green-700" :
                        p.status === "rejected" ? "bg-red-100 text-red-700" :
                        p.status === "publish_failed" ? "bg-amber-100 text-amber-700" :
                        p.status === "approved_no_creds" ? "bg-slate-200 text-slate-700" :
                        "bg-yellow-100 text-yellow-800"
                      }`} data-testid={`social-post-status-${p.id}`}>
                        {p.status}
                      </span>
                    </div>
                    <textarea
                      className="w-full min-h-[80px] rounded-md border border-slate-200 p-2 text-sm"
                      defaultValue={p.content}
                      disabled={p.status === "published"}
                      onBlur={(e) => e.target.value !== p.content && patchPost(p.id, e.target.value)}
                      data-testid={`social-post-content-${p.id}`}
                    />
                    {p.hashtags?.length > 0 && (
                      <div className="text-[11px] text-slate-500">{p.hashtags.join(" ")}</div>
                    )}
                    {p.error && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">
                        ⚠️ {p.error}
                      </div>
                    )}
                    {["pending", "approved_no_creds", "publish_failed"].includes(p.status) && (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => rejectPost(p.id)}
                          data-testid={`social-post-reject-${p.id}`}>Rifiuta</Button>
                        <Button size="sm" onClick={() => approvePost(p.id)}
                          className="bg-pink-600 hover:bg-pink-700 text-white"
                          data-testid={`social-post-approve-${p.id}`}>Approva e pubblica</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-slate-50 rounded-b-2xl">
          <Button variant="ghost" onClick={onClose} data-testid="social-config-cancel">Chiudi</Button>
          <Button onClick={save} disabled={saving}
            className="bg-pink-600 hover:bg-pink-700 text-white"
            data-testid="social-config-save">
            {saving ? "Salvataggio…" : "Salva configurazione"}
          </Button>
        </div>
      </div>
    </div>
  );
}

